import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { CartContext } from '../context/CartContext';
import { orderAPI, menuAPI } from '../utils/api';
import { formatCurrency } from '../utils/formatters';
import toast from '../utils/notifications';
import { dashboardRefreshEmitter } from '../utils/dashboardRefresh';
import {
  PrinterIcon,
  CheckCircleIcon,
  XMarkIcon,
  BanknotesIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';

const Checkout = () => {
  const { user } = useContext(AuthContext);
  const { items: cart, clear: clearCart } = useContext(CartContext);
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [transactionId, setTransactionId] = useState(null);
  const [orderTotal, setOrderTotal] = useState(0);
  const [orderItems, setOrderItems] = useState([]);
  const [orderStatus, setOrderStatus] = useState('pending');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amountPaid, setAmountPaid] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [takeoutCanisterCount, setTakeoutCanisterCount] = useState(0);

  const [orderSubtotal, setOrderSubtotal] = useState(0);
  const [orderDiscountAmount, setOrderDiscountAmount] = useState(0);
  const [orderDiscountPercentage, setOrderDiscountPercentage] = useState(0);

  const resolveCheckoutRestaurantId = () => {
    const fromUser = Number.parseInt(user?.restaurant_id, 10);
    if (fromUser === 1 || fromUser === 2) {
      return fromUser;
    }

    const fromStorage = Number.parseInt(localStorage.getItem('selected_restaurant_id'), 10);
    if (fromStorage === 1 || fromStorage === 2) {
      return fromStorage;
    }

    return 1;
  };


  // Check role access - Admin cannot checkout, only customer and staff can
  useEffect(() => {
    if (user && user.role === 'admin') {
      toast.error('Admins cannot place orders');
      navigate('/admin/dashboard');
    } else if (user && user.role !== 'customer' && user.role !== 'staff') {
      // Block any other unauthorized roles
      toast.error('You do not have permission to checkout');
      navigate('/');
    }
  }, [user, navigate]);

  // Note: Backend uses fresh database prices at order time
  // We don't fetch fresh prices here - checkout shows cart prices for reference only
  // The final receipt price is what the backend calculated, which is always correct
  useEffect(() => {
    // Fresh price fetch disabled - backend handles accurate pricing at order submission
    console.log('📋 Checkout loaded - backend will calculate correct prices at order time');
  }, []);

  const getItemKey = (item, index) => item.menu_item_id || item.id || index;

  // Calculate totals using CART prices for display only
  // Backend will recalculate with actual database prices at order time
  const totalAmount = cart.reduce((total, item) => {
    const price = parseFloat(item.price) || 0;
    const qty = parseInt(item.qty) || 0;
    return total + (price * qty);
  }, 0) + (takeoutCanisterCount * 25);

  // Log checkout details
  useEffect(() => {
    if (cart.length > 0) {
      console.log('🛒 Checkout page:', {
        cartItems: cart.length,
        cartTotal: totalAmount,
        note: 'This is displayed total from cart prices. Final receipt will show backend-calculated price.'
      });
    }
  }, [cart, totalAmount]);

  const change = amountPaid ? Math.max(0, parseFloat(amountPaid) - totalAmount) : 0;

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    try {
      setLoading(true);

      // Prepare order items - map cart structure to API format
      const items = cart.map((item, index) => {
        return {
          menu_item_id: item.menu_item_id || item.id,
          quantity: parseInt(item.qty) || 1,
          apply_discount: false
        };
      });

      console.log('Submitting order with items:', items);
      console.log('User info:', user);
      console.log('Auth token:', localStorage.getItem('auth_token'));

      // Create order
      const response = await orderAPI.create({
        items,
        restaurant_id: resolveCheckoutRestaurantId(),
        payment_method: paymentMethod,
        takeout_canister_count: takeoutCanisterCount
      });

      const order = response.data.data;
      setOrderId(order.order_id);
      setTransactionId(order.transaction_id);
      setOrderTotal(order.total_amount);
      setOrderSubtotal(order.subtotal || order.total_amount);
      setOrderDiscountAmount(order.discount_amount || 0);
      setOrderDiscountPercentage(order.discount_percentage || 0);
      setOrderItems(order.items || []);
      setOrderStatus(order.status || 'pending');
      setTakeoutCanisterCount(order.takeout_canister_count || takeoutCanisterCount);
      
      // For cash payments, order stays as "pending" - to be paid at counter
      // For online payments, redirect to payment gateway (to be implemented)
      if (paymentMethod === 'online') {
        // TODO: Redirect to payment gateway
        toast.info('Online payment will be implemented soon');
      }

      setOrderComplete(true);
      clearCart();
      toast.success('Order placed successfully!');
      
      // Trigger dashboard refresh for admin
      dashboardRefreshEmitter.emit();

    } catch (error) {
      console.error('Checkout error:', error);
      console.error('Error response:', error.response?.data);
      
      // Show specific error message from server if available
      const errorMsg = error.response?.data?.message || 'Failed to complete order';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const printReceipt = () => {
    const printWindow = window.open('', '_blank', 'width=302,height=500');
    const receiptHTML = generateReceiptHTML();
    
    printWindow.document.write(receiptHTML);
    printWindow.document.close();
    
    // Auto print after content loads
    printWindow.onload = function() {
      setTimeout(() => {
        printWindow.print();
      }, 250);
    };
    
    // Close window after print dialog closes (works on most browsers)
    printWindow.onafterprint = function() {
      printWindow.close();
    };
    
    // Fallback: close after 2 seconds if print dialog doesn't trigger onafterprint
    setTimeout(() => {
      try {
        if (printWindow && !printWindow.closed) {
          printWindow.close();
        }
      } catch (e) {
        // Window might have been closed already
      }
    }, 2000);
  };

  const generateReceiptHTML = () => {
    const now = new Date();
    const dateStr = now.toLocaleDateString();
    const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const receiptSubtotal = orderSubtotal || orderTotal || 0;
    const receiptDiscountAmount = orderDiscountAmount || 0;
    const receiptDiscountPercentage = receiptDiscountAmount > 0 ? (orderDiscountPercentage || 20) : 0;
    const receiptCanisterCost = takeoutCanisterCount * 25;

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Receipt - ${transactionId}</title>
        <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap" rel="stylesheet">
        <style>
          @page {
            size: 58mm auto;
            margin: 1mm;
          }
          
          body {
            font-family: Arial, Helvetica, sans-serif;
            font-size: 10pt;
            width: 56mm;
            margin: 0;
            padding: 1mm;
            line-height: 1.4;
          }
          
          .header {
            text-align: center;
            border-bottom: 2px dashed #000;
            padding-bottom: 2mm;
            margin-bottom: 2mm;
          }
          
          .logo {
            width: 22mm;
            height: 22mm;
            margin: 0 auto 1mm;
            border-radius: 50%;
            object-fit: cover;
          }
          
          .header h1 {
            font-family: 'Bebas Neue', cursive;
            font-size: 16pt;
            margin: 0 0 0.5mm 0;
            font-weight: bold;
            letter-spacing: 1.5px;
          }
          
          .header p {
            font-family: Arial, Helvetica, sans-serif;
            font-size: 9pt;
            margin: 0.5mm 0;
            font-weight: bold;
          }
          
          .info {
            font-size: 9pt;
            margin-bottom: 2mm;
            line-height: 1.5;
          }
          
          .items {
            border-top: 2px dashed #000;
            border-bottom: 2px dashed #000;
            padding: 2mm 0;
            margin: 2mm 0;
          }
          
          .item {
            display: flex;
            justify-content: space-between;
            margin: 1.5mm 0;
            font-size: 9pt;
            font-weight: 500;
          }
          
          .item-name {
            flex: 1;
            font-weight: normal;
          }
          
          .item-qty {
            width: 10mm;
            text-align: center;
            font-weight: normal;
          }
          
          .item-price {
            width: 18mm;
            text-align: right;
            font-weight: bold;
          }
          
          .totals {
            margin-top: 2mm;
            font-size: 10pt;
          }
          
          .total-row {
            display: flex;
            justify-content: space-between;
            margin: 1mm 0;
          }
          
          .total-row.grand {
            font-family: Arial, Helvetica, sans-serif;
            font-weight: bold;
            font-size: 13pt;
            border-top: 1px solid #000;
            padding-top: 1mm;
            margin-top: 2mm;
            letter-spacing: 0.5px;
          }
          
          .footer {
            text-align: center;
            margin-top: 3mm;
            padding-top: 2mm;
            border-top: 1px dashed #000;
            font-size: 9pt;
            line-height: 1.6;
          }
          
          .footer .bold {
            font-family: 'Bebas Neue', cursive;
            font-size: 13pt;
            letter-spacing: 2px;
            font-weight: bold;
          }
          
          .center {
            text-align: center;
          }
          
          .bold {
            font-weight: bold;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <img src="/images/casa-gatchalian-logo-receipt.jpg" alt="Casa Gatchalian" class="logo" />
          <h1>CASA GATCHALIAN</h1>
          <p>Restobar</p>
        </div>
        
        <div class="info">
          <div>Date: ${dateStr} ${timeStr}</div>
          <div>Order #: ${orderId}</div>
          <div>Transaction: ${transactionId}</div>
          <div>Ordered by: ${user.full_name || user.email}</div>
        </div>
        
        <div class="items">
          ${orderItems.map(item => `
            <div class="item">
              <div class="item-name">${item.menu_item?.name || 'Unknown'}</div>
              <div class="item-qty">x${item.quantity}</div>
              <div class="item-price">${formatCurrency(item.price * item.quantity, false)}</div>
            </div>
          `).join('')}
        </div>
        
        <div class="totals">
          ${takeoutCanisterCount > 0 ? `
            <div class="total-row">
              <span>Subtotal (Food):</span>
              <span>₱${parseFloat(receiptSubtotal - receiptCanisterCost).toFixed(2)}</span>
            </div>
            <div class="total-row">
              <span>Takeout Canister:</span>
              <span>₱${parseFloat(receiptCanisterCost).toFixed(2)}</span>
            </div>
          ` : ''}
          <div class="total-row">
            <span>Subtotal:</span>
            <span>₱${parseFloat(receiptSubtotal).toFixed(2)}</span>
          </div>
          ${receiptDiscountAmount > 0 ? `
            <div class="total-row" style="color: #16a34a; font-weight: bold;">
              <span>Discount (${receiptDiscountPercentage}%):</span>
              <span>-₱${parseFloat(receiptDiscountAmount).toFixed(2)}</span>
            </div>
          ` : ''}
          <div class="total-row grand">
            <span>TOTAL:</span>
            <span>₱${parseFloat(orderTotal).toFixed(2)}</span>
          </div>
          <div class="total-row">
            <span>Payment:</span>
            <span>${paymentMethod === 'cash' ? 'Cash' : 'Online Payment'}</span>
          </div>
          <div class="total-row">
            <span>Status:</span>
            <span>${orderStatus.charAt(0).toUpperCase() + orderStatus.slice(1)}</span>
          </div>
        </div>
        
        <div class="footer">
          <p class="bold">THANK YOU!</p>
          <p>Please come again</p>
          <p style="margin-top: 2mm; font-size: 7pt;">
            Not an official receipt for inventory use only.
          </p>
          <p style="margin-top: 2mm; border-bottom: 1px dashed #000; padding-bottom: 2mm;">&nbsp;</p>
          <p style="margin-top: 5mm;">&nbsp;</p>
        </div>
      </body>
      </html>
    `;
  };

  if (orderComplete) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <CheckCircleIcon className="w-20 h-20 text-green-500 mx-auto mb-4" />
            
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Order Complete!
            </h1>
            
            <div className="bg-gray-50 rounded-lg p-6 my-6">
              <p className="text-sm text-gray-600 mb-2">Order ID</p>
              <p className="text-4xl font-bold text-gray-900 mb-4">
                {orderId}
              </p>
              <p className="text-sm text-gray-600 mb-1">Transaction ID</p>
              <p className="text-lg text-gray-700 font-mono">
                {transactionId}
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="space-y-2">
                <div className="flex justify-between items-center text-sm text-gray-700">
                  <span>Subtotal:</span>
                  <span className="font-semibold">{formatCurrency(orderSubtotal || orderTotal)}</span>
                </div>
                {orderDiscountAmount > 0 && (
                  <div className="flex justify-between items-center text-sm text-green-600 font-semibold">
                    <span>Discount ({orderDiscountPercentage || 20}%):</span>
                    <span>-{formatCurrency(orderDiscountAmount)}</span>
                  </div>
                )}
                <div className="border-t border-blue-200 pt-2">
                  <p className="text-lg font-semibold text-blue-900 mb-1">
                    Total Amount
                  </p>
                  <p className="text-3xl font-bold text-blue-600">
                    {formatCurrency(orderTotal)}
                  </p>
                </div>
              </div>
            </div>

            {/* Print receipt button only for staff */}
            {user?.role === 'staff' && (
              <button
                onClick={() => {
                  printReceipt();
                }}
                className="inline-flex items-center px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 mb-4 text-lg font-semibold"
              >
                <PrinterIcon className="w-6 h-6 mr-2" />
                Print Receipt
              </button>
            )}

            <div className="flex gap-4 justify-center mt-6">
              <button
                onClick={() => {
                  setOrderComplete(false);
                  setOrderId(null);
                  setTransactionId(null);
                  setOrderTotal(0);
                  setOrderSubtotal(0);
                  setOrderDiscountAmount(0);
                  setOrderDiscountPercentage(0);
                  setOrderItems([]);
                  navigate('/menu');
                }}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                New Order
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Back to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-3 sm:py-6">
      <div className="max-w-3xl mx-auto px-3 sm:px-4">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 sm:px-5 py-3 sm:py-4">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-white">Checkout</h1>
                <p className="text-blue-100 text-xs mt-0.5">Complete your order</p>
              </div>
              <button
                onClick={() => navigate('/cart')}
                className="text-white/90 hover:text-white hover:bg-white/10 p-1.5 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-4 sm:p-5">
            {/* Order Items */}
            <div className="mb-4 sm:mb-5">
              <h2 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center">
                <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center mr-2">
                  <span className="text-blue-600 font-bold text-xs">1</span>
                </div>
                Order Summary
              </h2>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="space-y-2">
                  {cart.map((item, index) => {
                    const price = parseFloat(item.price) || 0;
                    const qty = parseInt(item.qty) || 0;
                    const lineTotal = price * qty;

                    return (
                      <div key={getItemKey(item, index)} className="flex justify-between items-start gap-2 py-2 border-b border-gray-200 last:border-0">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 text-base sm:text-lg truncate">{item.title}</p>
                          <p className="text-sm text-gray-600 mt-0.5">
                            {formatCurrency(price)} × {qty}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <p className="font-bold text-gray-900 text-base sm:text-lg whitespace-nowrap">
                            {formatCurrency(lineTotal)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-2 pt-2 border-t-2 border-gray-300">
                  <div className="flex justify-between items-center">
                    <span className="text-sm sm:text-base font-semibold text-gray-700">Total</span>
                    <span className="text-base sm:text-lg font-bold text-gray-900">{formatCurrency(totalAmount)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Takeout Canister */}
            <div className="mb-4 sm:mb-5 bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <label className="block text-sm sm:text-base font-semibold text-gray-900">Add Takeout Canister</label>
                  <p className="text-xs sm:text-sm text-gray-600 mt-0.5">₱25 per canister</p>
                </div>
                <div className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg">
                  <button
                    onClick={() => setTakeoutCanisterCount(Math.max(0, takeoutCanisterCount - 1))}
                    className="px-3 py-2 text-gray-600 hover:bg-gray-100 transition-colors font-semibold"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    value={takeoutCanisterCount}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 0;
                      setTakeoutCanisterCount(Math.max(0, value));
                    }}
                    className="w-12 text-center py-2 border-0 outline-none font-semibold text-gray-900 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    min="0"
                  />
                  <button
                    onClick={() => setTakeoutCanisterCount(takeoutCanisterCount + 1)}
                    className="px-3 py-2 text-gray-600 hover:bg-gray-100 transition-colors font-semibold"
                  >
                    +
                  </button>
                </div>
              </div>
              {takeoutCanisterCount > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-700 font-medium">{takeoutCanisterCount} canister{takeoutCanisterCount !== 1 ? 's' : ''}</span>
                    <span className="text-sm font-semibold text-gray-900">₱{(takeoutCanisterCount * 25).toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>


            {/* Payment Method */}
            <div className="mb-4 sm:mb-5">
              <h2 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center">
                <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center mr-2">
                  <span className="text-blue-600 font-bold text-xs">2</span>
                </div>
                Payment Method
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setPaymentMethod('cash')}
                  className={`p-3 sm:p-4 border-2 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                    paymentMethod === 'cash'
                      ? 'border-blue-600 bg-blue-50 text-blue-600 shadow-md scale-[1.02]'
                      : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <BanknotesIcon className="w-5 h-5" />
                  Cash Payment
                </button>
                <button
                  onClick={() => setPaymentMethod('online')}
                  className={`p-3 sm:p-4 border-2 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                    paymentMethod === 'online'
                      ? 'border-blue-600 bg-blue-50 text-blue-600 shadow-md scale-[1.02]'
                      : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <CreditCardIcon className="w-5 h-5" />
                  Online Payment
                </button>
              </div>

              {/* Cash Payment Info - For Customers */}
              {paymentMethod === 'cash' && user?.role === 'customer' && (
                <div className="mt-3 p-3 sm:p-4 bg-yellow-50 border-2 border-yellow-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <div className="flex-shrink-0 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-bold">i</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs sm:text-sm font-semibold text-yellow-900 mb-1">Pay at Counter</p>
                      <p className="text-xs text-yellow-700">
                        Please proceed to the counter with your Order ID to complete payment with the cashier.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Cash Payment Amount - For Staff Only */}
              {paymentMethod === 'cash' && user?.role === 'staff' && (
                <div className="mt-4 sm:mt-5 bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
                  <label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-2">
                    Amount Paid
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-base font-semibold">₱</span>
                    <input
                      type="number"
                      step="0.01"
                      value={amountPaid}
                      onChange={(e) => setAmountPaid(e.target.value)}
                      placeholder="0.00"
                      className="w-full pl-8 pr-3 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base font-semibold transition-all [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                  {amountPaid && parseFloat(amountPaid) > 0 && (
                    <div className="mt-2 space-y-2">
                      <div className="p-2 bg-white border border-green-300 rounded-lg">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-700 font-medium text-xs sm:text-sm">Total:</span>
                          <span className="text-gray-900 font-bold text-sm sm:text-base">{formatCurrency(totalAmount)}</span>
                        </div>
                      </div>
                      {parseFloat(amountPaid) >= totalAmount && (
                        <div className="p-2 bg-green-100 border border-green-300 rounded-lg">
                          <div className="flex justify-between items-center">
                            <span className="text-green-700 font-semibold text-xs sm:text-sm">Change:</span>
                            <span className="text-green-600 font-bold text-sm sm:text-base">{formatCurrency(change)}</span>
                          </div>
                        </div>
                      )}
                      {parseFloat(amountPaid) < totalAmount && (
                        <div className="p-2 bg-red-100 border border-red-300 rounded-lg">
                          <div className="flex justify-between items-center">
                            <span className="text-red-700 font-semibold text-xs sm:text-sm">Short:</span>
                            <span className="text-red-600 font-bold text-sm sm:text-base">{formatCurrency(totalAmount - parseFloat(amountPaid))}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Online Payment Fields */}
            {paymentMethod === 'online' && (
              <div className="mb-4 sm:mb-5">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-3 sm:p-4">
                  <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-3 flex items-center">
                    <div className="w-6 h-6 bg-blue-600 text-white rounded-lg flex items-center justify-center mr-2">
                      <CreditCardIcon className="w-4 h-4" />
                    </div>
                    Scan the QR Code
                  </h3>
                  
                  {/* QR Code */}
                  <div className="bg-white rounded-lg p-3 mb-3 shadow-sm">
                    <div className="flex flex-col items-center justify-center">
                      <div className="w-48 h-48 sm:w-52 sm:h-52 bg-white rounded-lg shadow-inner flex items-center justify-center p-2 border-2 border-gray-300">
                        <img 
                          src="/images/instapay.jpg" 
                          alt="Online Payment QR Code"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Reference Number Input */}
                  <div className="mt-3">
                    <label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-1.5">
                        Reference Number *
                    </label>
                    <input
                      type="text"
                      value={referenceNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 13);
                        setReferenceNumber(value);
                      }}
                      placeholder="Enter 13-digit reference number"
                      maxLength="13"
                      className="w-full px-3 py-2.5 border-2 border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all"
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      {referenceNumber.length}/13 digits
                    </p>
                  </div>
                </div>
              </div>
            )}



            {/* Total */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4 shadow-md">
              <div className="space-y-1">
                <div className="flex justify-between items-center border-t border-blue-400 pt-1">
                  <span className="text-blue-100 text-sm sm:text-base font-medium">Total Amount</span>
                  <span className="text-white text-xl sm:text-2xl font-bold">{formatCurrency(totalAmount)}</span>
                </div>
              </div>
            </div>

            {/* Complete Order Button */}
            <button
              onClick={handleCheckout}
              disabled={loading || cart.length === 0}
              className="w-full py-3 sm:py-4 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-bold text-sm sm:text-base hover:from-green-700 hover:to-green-800 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg transform hover:scale-[1.01] active:scale-[0.99]"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Processing...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <CheckCircleIcon className="w-5 h-5" />
                  Complete Order
                </span>
              )}
            </button>

            <p className="text-center text-xs text-gray-500 mt-3">
              By completing this order, you agree to our terms and conditions
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
